import {
  CurrencyCode,
  enumPaymentStatus,
  enumPaymentType,
} from '@rematter/frontend-lib-api-client';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export type UnappliedAdvanceForBooking = {
  id: string;
  documentNumber: string;
  amount: { amount: number; currencyCode: CurrencyCode };
  unappliedAmount: { amount: number; currencyCode: CurrencyCode };
  transactionDate: string;
  accountId: string;
  accountName: string;
};

type QueryParams = {
  accountIds: string[];
};

export const unappliedAdvancesForAccountsQueryAtom = createQueryAtomFactory(
  'trade.unappliedAdvancesForAccounts',
  (params: QueryParams) => ({
    payments: {
      __args: {
        filter: {
          accountIDs: params.accountIds,
          type: [enumPaymentType.AdvanceIn],
          status: [enumPaymentStatus.Issued, enumPaymentStatus.Posted],
          minUnappliedBalance: 0.01,
        },
        pagination: { offset: 0, limit: 100 },
      },
      entries: {
        id: true,
        documentNumber: true,
        amount: {
          amount: true,
          currencyCode: true,
        },
        unappliedAmount: {
          amount: true,
          currencyCode: true,
        },
        transactionDate: true,
        account: {
          id: true,
          name: true,
        },
      },
    },
  }),
  {
    select: (data): UnappliedAdvanceForBooking[] => {
      const entries = data?.payments?.entries;
      if (!entries) {
        return [];
      }

      return entries
        .filter(
          (
            entry,
          ): entry is typeof entry & {
            amount: NonNullable<typeof entry.amount> & { currencyCode: string };
            unappliedAmount: NonNullable<typeof entry.unappliedAmount> & {
              currencyCode: string;
            };
            account: NonNullable<typeof entry.account> & { id: string };
          } =>
            !!entry.amount?.currencyCode &&
            !!entry.unappliedAmount?.currencyCode &&
            !!entry.account?.id,
        )
        .map((entry) => ({
          id: entry.id,
          documentNumber: entry.documentNumber,
          amount: {
            amount: entry.amount.amount ?? 0,
            currencyCode: entry.amount.currencyCode as CurrencyCode,
          },
          unappliedAmount: {
            amount: entry.unappliedAmount.amount ?? 0,
            currencyCode: entry.unappliedAmount.currencyCode as CurrencyCode,
          },
          transactionDate: entry.transactionDate ?? '',
          accountId: entry.account.id,
          accountName: entry.account?.name ?? '',
        }));
    },
  },
);
